import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "@/components/ui/dialog/Dialog";
import { Button } from "@/components/ui/button/Button";
import type { ExposureBandsMap } from "../adapters/echarts/types";

export interface ExposureBandsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  categories: string[];
  savedBands: ExposureBandsMap | null;
  onSave: (bands: ExposureBandsMap) => { success: boolean; error?: string };
  onClear: () => void;
}

type DraftRow = {
  min: string;
  max: string;
  target: string;
};

function buildDraftFromBands(
  categories: string[],
  savedBands: ExposureBandsMap | null
): Record<string, DraftRow> {
  const draft: Record<string, DraftRow> = {};
  for (const category of categories) {
    const saved = savedBands?.[category];
    draft[category] = {
      min: saved?.min !== undefined ? String(saved.min) : "",
      max: saved?.max !== undefined ? String(saved.max) : "",
      target: saved?.target !== undefined ? String(saved.target) : "",
    };
  }
  return draft;
}

export function ExposureBandsDialog({
  isOpen,
  onClose,
  categories,
  savedBands,
  onSave,
  onClear,
}: ExposureBandsDialogProps) {
  const [draft, setDraft] = useState<Record<string, DraftRow>>({});
  const [error, setError] = useState<string | null>(null);

  const categoriesKey = useMemo(() => categories.join("|"), [categories]);

  useEffect(() => {
    if (isOpen) {
      setDraft(buildDraftFromBands(categories, savedBands));
      setError(null);
    }
  }, [isOpen, categoriesKey, savedBands, categories]);

  const handleFieldChange = (
    category: string,
    field: keyof DraftRow,
    value: string
  ) => {
    setDraft((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value,
      },
    }));
    setError(null);
  };

  const handleSave = () => {
    const bands: ExposureBandsMap = {};
    for (const category of categories) {
      const row = draft[category];
      bands[category] = {
        min: Number(row?.min),
        max: Number(row?.max),
        target: Number(row?.target),
      };
    }

    const result = onSave(bands);
    if (result.success) {
      onClose();
    } else {
      setError(result.error || "Failed to save bands.");
    }
  };

  const handleClear = () => {
    onClear();
    setDraft(buildDraftFromBands(categories, null));
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} size="4xl">
      <DialogTitle>Configure Exposure Bands</DialogTitle>
      <DialogBody>
        {categories.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Load chart data first to configure min, max, and target values for
            each category.
          </p>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Enter min, max, and target (%) for every category. All fields are
              required.
            </p>
            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
                {error}
              </div>
            )}
            <div className="max-h-[50vh] overflow-y-auto overflow-x-hidden rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="w-full table-fixed divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800/60 sticky top-0">
                  <tr>
                    <th className="w-[55%] px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-200">
                      Category
                    </th>
                    <th className="w-[15%] px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">
                      Min
                    </th>
                    <th className="w-[15%] px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">
                      Max
                    </th>
                    <th className="w-[15%] px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">
                      Target
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {categories.map((category) => (
                    <tr key={category}>
                      <td className="px-4 py-2 text-gray-900 dark:text-gray-100 align-top">
                        {category}
                      </td>
                      {(["min", "max", "target"] as const).map((field) => (
                        <td key={field} className="px-3 py-2 align-top">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            step={0.1}
                            value={draft[category]?.[field] ?? ""}
                            onChange={(e) =>
                              handleFieldChange(category, field, e.target.value)
                            }
                            className="w-full min-w-[4.5rem] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="%"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogBody>
      <DialogActions>
        <Button outline onClick={onClose}>
          Cancel
        </Button>
        {savedBands && (
          <Button outline onClick={handleClear}>
            Clear
          </Button>
        )}
        <Button color="indigo" onClick={handleSave} disabled={categories.length === 0}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
